import type { MouseEvent } from 'react'
import { useRef, useState } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import capitalize from 'lodash/capitalize'
import { FormProvider, useForm } from 'react-hook-form'

import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { getLocalDateTime } from '@/utils/date.ts'

import { useShiftMutations } from '../../hooks/use-shift-mutations'
import type {
  EditShiftDialogProps,
  MutateRepeatableShiftTarget,
} from '../../types'
import { getRepetitionValue, normalizeShiftMutationData } from '../../utils'
import { CreateEditShiftForm } from '../create-edit-form'
import { ShiftFormSchema } from '../create-edit-form/consts'
import type { ShiftFormValues, Weekday } from '../create-edit-form/types'

export const EditShiftDialog = ({
  isModalVisible,
  onClose,
  shift,
  repeatDays,
  creators,
}: EditShiftDialogProps) => {
  const formRef = useRef<HTMLFormElement>(null)
  const { editRepeatableShift, editSingleShift, isEditing } =
    useShiftMutations()
  const [showSaveOptions, setShowSaveOptions] = useState(false)
  const editTargetRef = useRef<MutateRepeatableShiftTarget>('Shift')

  const defaultValues: ShiftFormValues = {
    startedAt: getLocalDateTime(shift.startedAt),
    finishedAt: getLocalDateTime(shift.finishedAt),
    creatorId: String(shift.creatorId),
    memberIds: shift.members.map(({ id }) => String(id)),
    isRepetitionInfinite: String(!shift.repeatedUntil),
    days: shift.days?.map(capitalize) as Weekday[],
    recurring: getRepetitionValue(repeatDays),
    endDate: shift.repeatedUntil
      ? getLocalDateTime(shift.repeatedUntil)
      : undefined,
  }

  const form = useForm<ShiftFormValues>({
    mode: 'onChange',
    defaultValues,
    resolver: zodResolver(ShiftFormSchema),
  })

  const onSubmit = (values: ShiftFormValues) => {
    const { isRepeatable, data } = normalizeShiftMutationData(values)

    return isRepeatable
      ? editRepeatableShift(
          {
            data,
            originalShift: shift,
            target: editTargetRef.current,
          },
          { onSuccess: onClose },
        )
      : editSingleShift(
          {
            data,
            originalShift: shift,
          },
          { onSuccess: onClose },
        )
  }

  const handleSaveClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (showSaveOptions) {
      event.preventDefault()

      return setShowSaveOptions(false)
    }

    const dirtyFields = Object.keys(form.formState.dirtyFields).filter(
      (fieldName) => fieldName !== 'endDate',
    )

    if (!shift.repeatableShiftId || !dirtyFields.length) return

    event.preventDefault()
    setShowSaveOptions(true)
  }

  const setTarget = (target: MutateRepeatableShiftTarget) => {
    setShowSaveOptions(false)
    editTargetRef.current = target
    formRef.current?.requestSubmit()
  }

  const submitButton = (
    <DropdownMenu open={showSaveOptions}>
      <DropdownMenuTrigger>
        <Button
          className="w-full"
          disabled={!form.formState.isDirty}
          isLoading={isEditing}
          onClick={handleSaveClick}
        >
          Save
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="z-[70] p-0">
        <DropdownMenuGroup>
          <DropdownMenuItem
            className="py-3 px-4"
            onClick={() => setTarget('Shift')}
          >
            Apply changes to just this shift
          </DropdownMenuItem>
          <DropdownMenuItem
            className="py-3 px-4"
            onClick={() => setTarget('All')}
          >
            Apply changes to all shifts afterwards
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )

  return (
    <Dialog
      show={isModalVisible}
      onClose={onClose}
      bigDialog
      panelClassName="sm:my-0 max-h-full overflow-auto"
    >
      <FormProvider {...form}>
        <form
          ref={formRef}
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <CreateEditShiftForm
            isEdit
            title="Edit Shift"
            onClose={onClose}
            creators={creators}
            submitButton={submitButton}
          />
        </form>
      </FormProvider>
    </Dialog>
  )
}
